// Gráficos com DADOS SIMULADOS — exigem dado em nível de chamado (datas,
// ID de instalação, par abertura↔fechamento) que não está no xlsx.
// Componentes reutilizáveis, distribuídos entre as abas (sempre com chip "simulado").
import { useMemo } from "react";
import { alpha } from "@mui/material/styles";
import { Box, Chip, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Layer,
  Line,
  LineChart,
  ReferenceLine,
  Rectangle,
  ResponsiveContainer,
  Sankey,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatInt } from "./shared";
import {
  cronicos,
  heatmapCidadeMotivo,
  reincidenciaPorDia,
  sankeyMotivos,
  spcSemanal,
} from "./simulatedData";

const shortName = (nome, max = 22) => {
  const s = String(nome || "").replace(/^[A-Z]{2}\d{2}-[A-Z-]+\s*-?\s*/i, "");
  const base = s || String(nome || "");
  return base.length > max ? `${base.slice(0, max)}…` : base;
};

export const SimChip = ({ theme, label = "dados simulados" }) => (
  <Chip
    size="small"
    label={label}
    sx={{
      height: 22,
      fontSize: 10,
      fontWeight: 700,
      color: theme.palette.warning.main,
      backgroundColor: alpha(theme.palette.warning.main, 0.14),
    }}
  />
);

/* ----------------------- curva de reincidência por dia ----------------------- */
export function CurvaReincidencia({ styles, theme }) {
  return (
    <Box sx={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={reincidenciaPorDia} margin={{ left: 0, right: 12, top: 8, bottom: 4 }}>
          <defs>
            <linearGradient id="gradReincLab" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.palette.error.main} stopOpacity={0.35} />
              <stop offset="100%" stopColor={theme.palette.error.main} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="dia"
            ticks={[1, 15, 30, 45, 60, 75, 90]}
            tickFormatter={(v) => `${v}d`}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            formatter={(value) => [`${value}%`, "Acumulado"]}
            labelFormatter={(l) => `Dia ${l}`}
          />
          {[30, 60, 90].map((d) => (
            <ReferenceLine
              key={d}
              x={d}
              stroke={alpha(theme.palette.text.primary, 0.25)}
              strokeDasharray="4 4"
              label={{
                value: `${d}d`,
                position: "top",
                fontSize: 10,
                fill: theme.palette.text.secondary,
              }}
            />
          ))}
          <Area
            type="monotone"
            dataKey="acumulado"
            stroke={theme.palette.error.main}
            strokeWidth={2}
            fill="url(#gradReincLab)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}

/* ----------------------- carta de controle (SPC) ----------------------- */
export function CartaControle({ styles, theme }) {
  const forced = spcSemanal.filter((s) => s.foraControle);
  return (
    <Box sx={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={spcSemanal} margin={{ left: 0, right: 12, top: 8, bottom: 4 }}>
          <XAxis
            dataKey="semana"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => `${v}%`}
            domain={["dataMin - 3", "dataMax + 3"]}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            formatter={(value, name) => [`${value}%`, name]}
          />
          <ReferenceLine
            y={spcSemanal[0]?.ucl}
            stroke={theme.palette.error.main}
            strokeDasharray="5 5"
            label={{ value: "UCL", position: "right", fontSize: 10, fill: theme.palette.error.main }}
          />
          <ReferenceLine
            y={spcSemanal[0]?.media}
            stroke={alpha(theme.palette.text.primary, 0.4)}
            label={{ value: "Média", position: "right", fontSize: 10, fill: theme.palette.text.secondary }}
          />
          <ReferenceLine
            y={spcSemanal[0]?.lcl}
            stroke={theme.palette.success.main}
            strokeDasharray="5 5"
            label={{ value: "LCL", position: "right", fontSize: 10, fill: theme.palette.success.main }}
          />
          <Line
            type="monotone"
            dataKey="pct"
            name="Reincidência"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
      {forced.length > 0 && (
        <Typography sx={{ ...styles.cardSubtitle, mt: 0.5 }}>
          ⚠ {forced.map((f) => f.semana).join(", ")} fora de controle (acima da UCL)
        </Typography>
      )}
    </Box>
  );
}

/* ----------------------- Sankey Abertura → Fechamento ----------------------- */
// Paleta de cores distinta por nó — deixa o fluxo mais legível e colorido.
const sankeyPalette = (theme) => [
  theme.palette.primary.main,
  theme.palette.error.main,
  theme.palette.warning.main,
  theme.palette.success.main,
  theme.palette.info.main,
  theme.palette.secondary.main,
  "#8e44ad",
  "#16a085",
  "#e67e22",
  "#2980b9",
  "#c0392b",
];

const sankeyNodeColor = (theme, index) => {
  const palette = sankeyPalette(theme);
  return palette[index % palette.length];
};

function SankeyNode({ x, y, width, height, index, payload, theme }) {
  const isLeft = payload.depth === 0;
  const color = sankeyNodeColor(theme, index);
  return (
    <Layer>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        fillOpacity={0.95}
        radius={2}
      />
      <text
        x={isLeft ? x + width + 6 : x - 6}
        y={y + height / 2}
        textAnchor={isLeft ? "start" : "end"}
        dominantBaseline="middle"
        fontSize={11}
        fill={theme.palette.text.primary}
      >
        {payload.name}
      </text>
    </Layer>
  );
}

// Link colorido pela cor do nó de origem.
function SankeyLink({ sourceX, sourceY, sourceControlX, targetX, targetY, targetControlX, linkWidth, payload, theme }) {
  const color = sankeyNodeColor(theme, payload?.source?.index ?? 0);
  return (
    <Layer>
      <path
        d={`
          M${sourceX},${sourceY}
          C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
        `}
        fill="none"
        stroke={color}
        strokeWidth={linkWidth}
        strokeOpacity={0.4}
      />
    </Layer>
  );
}

export function SankeyMotivos({ styles, theme }) {
  return (
    <Box sx={{ ...styles.chartBox, height: 360 }}>
      <ResponsiveContainer width="100%" height="100%">
        <Sankey
          data={sankeyMotivos}
          nodePadding={26}
          nodeWidth={12}
          margin={{ left: 8, right: 8, top: 12, bottom: 12 }}
          link={<SankeyLink theme={theme} />}
          node={<SankeyNode theme={theme} />}
        >
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            formatter={(value) => [formatInt(value), "Chamados"]}
          />
        </Sankey>
      </ResponsiveContainer>
    </Box>
  );
}

/* ----------------------- Heatmap Cidade × Motivo ----------------------- */
export function Heatmap({ theme }) {
  const { cidades: cs, motivos, matrix } = heatmapCidadeMotivo;
  const max = Math.max(...matrix.flat());
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `minmax(150px, 1.4fr) repeat(${motivos.length}, 1fr)`,
          gap: "4px",
          minWidth: 520,
        }}
      >
        <Box />
        {motivos.map((m) => (
          <Box
            key={m}
            sx={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "uppercase",
              color: theme.palette.text.secondary,
              textAlign: "center",
              alignSelf: "end",
              pb: "4px",
            }}
          >
            {m}
          </Box>
        ))}
        {cs.map((cidade, i) => (
          <Box key={cidade} sx={{ display: "contents" }}>
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: theme.palette.text.primary,
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
              }}
            >
              {cidade}
            </Box>
            {matrix[i].map((v, j) => {
              const ratio = max ? v / max : 0;
              return (
                <Box
                  key={j}
                  title={`${cidade} • ${motivos[j]}: ${formatInt(v)}`}
                  sx={{
                    height: 40,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    color:
                      ratio > 0.55 ? theme.palette.common?.white || "#fff" : theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.error.main, 0.12 + ratio * 0.7),
                  }}
                >
                  {formatInt(v)}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ----------------------- multi-reincidência / crônicos ----------------------- */
export function CronicosDistribuicao({ styles, theme }) {
  return (
    <Box sx={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={cronicos.distribuicao} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
          <XAxis
            dataKey="faixa"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            formatter={(value) => [formatInt(value), "Instalações"]}
          />
          <Bar dataKey="clientes" radius={[6, 6, 0, 0]} barSize={48}>
            {cronicos.distribuicao.map((d, i) => (
              <Cell key={i} fill={i >= 2 ? theme.palette.error.main : theme.palette.primary.main} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export function CronicosTabela({ theme }) {
  const cellHead = {
    textAlign: "left",
    padding: "8px 10px",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
  };
  const cellBody = (idx) => ({
    padding: "8px 10px",
    fontSize: 13,
    borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
    backgroundColor: idx % 2 === 1 ? alpha(theme.palette.text.primary, 0.02) : "transparent",
  });
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={cellHead}>Instalação</Box>
            <Box component="th" sx={cellHead}>Cidade</Box>
            <Box component="th" sx={cellHead}>Filial</Box>
            <Box component="th" sx={{ ...cellHead, textAlign: "right" }}>Retornos</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {cronicos.top.map((c, idx) => (
            <Box component="tr" key={c.instalacao}>
              <Box component="td" sx={{ ...cellBody(idx), fontWeight: 700 }}>
                {c.instalacao}
              </Box>
              <Box component="td" sx={cellBody(idx)}>{c.cidade}</Box>
              <Box component="td" sx={cellBody(idx)}>{c.filial}</Box>
              <Box component="td" sx={{ ...cellBody(idx), textAlign: "right" }}>
                <Chip
                  size="small"
                  label={`${c.retornos}x`}
                  sx={{
                    height: 22,
                    fontWeight: 800,
                    color: theme.palette.error.main,
                    backgroundColor: alpha(theme.palette.error.main, 0.14),
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* ----------------------- ranking de variação mensal ----------------------- */
export function RankingVariacao({ styles, theme, trend }) {
  const data = useMemo(() => {
    const pioras = [...trend].sort((a, b) => b.delta - a.delta).slice(0, 8);
    const melhoras = [...trend].sort((a, b) => a.delta - b.delta).slice(0, 8);
    return [...pioras, ...melhoras.reverse()].map((t) => ({
      label: shortName(t.nome),
      delta: t.delta,
    }));
  }, [trend]);

  return (
    <Box sx={{ ...styles.chartBox, height: Math.max(data.length * 26 + 24, 320) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 32, top: 4, bottom: 4 }}>
          <XAxis
            type="number"
            tickFormatter={(v) => `${v > 0 ? "+" : ""}${v}`}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={150}
            tick={{ fontSize: 10, fill: theme.palette.text.primary }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
            formatter={(value) => [
              `${value > 0 ? "+" : ""}${value} p.p.`,
              value > 0 ? "Piorou" : "Melhorou",
            ]}
          />
          <ReferenceLine x={0} stroke={alpha(theme.palette.text.primary, 0.3)} />
          <Bar dataKey="delta" radius={[0, 4, 4, 0]} barSize={14}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.delta > 0 ? theme.palette.error.main : theme.palette.success.main} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

/* ----------------------- score de risco composto ----------------------- */
const DIA = 86400;
const calcScore = (row, delta) => {
  const cReinc = 40 * Math.min((row.pct || 0) / 0.5, 1);
  const cVol = 25 * Math.min((row.volume || 0) / 300, 1);
  const cTmt = 15 * Math.min((row.tmt || 0) / (3 * DIA), 1);
  const cTrend = 20 * Math.max(0, Math.min((Number(delta || 0) + 10) / 20, 1));
  return {
    reinc: +cReinc.toFixed(1),
    volume: +cVol.toFixed(1),
    tmt: +cTmt.toFixed(1),
    trend: +cTrend.toFixed(1),
    score: +(cReinc + cVol + cTmt + cTrend).toFixed(1),
  };
};

export function ScoreRisco({ styles, theme, rows, trend }) {
  const data = useMemo(() => {
    const trendMap = new Map((trend || []).map((t) => [t.nome, t]));
    return rows
      .map((r) => {
        const c = calcScore(r, trendMap.get(r.nome)?.delta);
        return { label: shortName(r.nome), ...c };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .reverse();
  }, [rows, trend]);

  const segs = [
    { key: "reinc", label: "Reincidência (40)", color: theme.palette.error.main },
    { key: "volume", label: "Volume (25)", color: theme.palette.primary.main },
    { key: "trend", label: "Tendência (20)", color: theme.palette.warning.main },
    { key: "tmt", label: "TMT (15)", color: theme.palette.info.main },
  ];

  return (
    <Box sx={{ ...styles.chartBox, height: Math.max(data.length * 30 + 40, 320) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 40, top: 4, bottom: 4 }}>
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={150}
            tick={{ fontSize: 10, fill: theme.palette.text.primary }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
          />
          {segs.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId="score"
              fill={s.color}
              radius={s.key === "tmt" ? [0, 4, 4, 0] : 0}
              barSize={18}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
